// src/pages/CharacterDesigner.jsx
// Родитель: мастер создания + управление персонажами + редактор.
// После успешного создания переключаем вкладку на «Управление».
// Admin/superadmin видит своих и чужих персонажей + действия (освободить, сменить владельца).

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  RadioGroup,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Gamepad as GamepadIcon,
  LocationOn as LocationOnIcon,
  RocketLaunch as RocketLaunchIcon,
  Explore as ExploreIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Flight as FlightIcon,
  Business as BusinessIcon,
  Fort as FortIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  People as PeopleIcon
} from '@mui/icons-material';

import {
  loadCharacters,
  deleteCharacter,
  clearError,
  clearSuccess,
  setSuccess,
  setError,
  setSelectedCharacter
} from '../store/slices/gameSlice';

import CharacterCreation from '../components/character_creation/CharacterCreation';
import CharacterCard from '../components/character_card/CharacterCard';
import CharacterAdminActions from '../components/character_card/CharacterAdminActions';
import CharacterEditor from '../components/character_editor/CharacterEditor';
import Gateway from '../api/gateway';
import WorldGateway from '../api/worldGateway';

// ========================
// КОНСТАНТЫ
// ========================

const TABS = {
  CREATION: 0,
  MANAGEMENT: 1,
  EDITOR: 2,
};

const STATUS_COLORS = {
  design: 'default',
  active: 'success',
  placed: 'primary',
  dead: 'error',
  archived: 'warning',
  emancipated: 'info',
};

// ========================
// УТИЛИТЫ ID
// ========================

const extractId = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d+$/.test(value)) return value;
  if (typeof value === 'number') return value.toString();
  if (value && typeof value === 'object') {
    return (
      extractId(value.id) ||
      extractId(value.character_id) ||
      extractId(value._id) ||
      extractId(value.image_bit_id)
    );
  }
  return null;
};

const validateStringId = (id) =>
  id !== null && id !== undefined && typeof id === 'string' && id.length > 0;

const validateId = (id) => {
  const extracted = extractId(id);
  return extracted !== null && typeof extracted === 'string' && extracted.length > 0;
};

const normalizeId = (id) => {
  if (!id) return null;
  if (typeof id === 'string') return id;
  if (typeof id === 'number') return id.toString();
  if (typeof id === 'object') return extractId(id);
  return String(id);
};

const isCharacterActive = (character) => {
  if (!character) return false;
  const status = character.status || character.character_status;
  return status === 'active' || status === 'ACTIVE' || character.is_active === true;
};

const isCharacterPlacedInWorld = (character) => {
  if (!character) return false;
  return (
    character.spawn_object_id &&
    character.spawn_object_id !== 'null' &&
    character.current_world_id &&
    character.current_world_id !== 'null' &&
    (character.in_world === true || character.placed_in_world === true)
  );
};

// ========================
// API АКТИВАЦИИ/ДЕАКТИВАЦИИ/РАЗМЕЩЕНИЯ
// ========================

const activateCharacterAPI = async (characterId) => {
  const id = normalizeId(characterId);
  if (!validateId(id)) throw new Error(`Invalid character ID: ${characterId}`);
  const response = await Gateway.api.activeCharacter.activateCharacter(id);
  return { success: true, data: response.data || response };
};

const deactivateCharacterAPI = async (characterId) => {
  const id = normalizeId(characterId);
  if (!validateId(id)) throw new Error(`Invalid character ID: ${characterId}`);
  const response = await Gateway.api.activeCharacter.deactivateCharacter(id);
  return { success: true, data: response.data || response };
};

const placeCharacterInSpawnPointAPI = async (characterId, spawnObjectId) => {
  const charId = extractId(characterId);
  if (!validateId(charId)) throw new Error(`Invalid character ID: ${characterId}`);
  if (!validateStringId(spawnObjectId)) throw new Error(`Invalid spawn object ID: ${spawnObjectId}`);
  return Gateway.api.activeCharacter.placeInSpawnPoint(charId, spawnObjectId);
};

// ========================
// ТИП ТОЧКИ СПАУНА
// ========================

const getSpawnPointType = (point) => {
  if (point.type && point.type !== 'unknown' && point.type !== ':unknown') {
    return typeof point.type === 'string' ? point.type : String(point.type).replace(':', '');
  }
  const id = (point.id || '').toLowerCase();
  const name = (point.name || '').toLowerCase();
  if (id.includes('carrier') || id.includes('ship') || id.includes('destroyer') || id.includes('frigate') || name.includes('авианосец') || name.includes('носитель') || name.includes('корабль')) return 'Корабль';
  if (id.includes('station') || id.includes('hub') || name.includes('станция') || name.includes('центр')) return 'Орбитальная станция';
  if (id.includes('port') || id.includes('dock') || name.includes('космопорт') || name.includes('порт') || name.includes('терминал')) return 'Космопорт';
  if (id.includes('military') || id.includes('camp') || id.includes('base') || id.includes('claw') || name.includes('лагерь') || name.includes('база') || name.includes('военный')) return 'Военный объект';
  if (id.includes('colony') || id.includes('outpost') || name.includes('колония') || name.includes('форпост')) return 'Колония';
  if (id.includes('mining') || name.includes('добывающий') || name.includes('шахта')) return 'Промышленный объект';
  if (id.includes('trader') || id.includes('merchant') || name.includes('торговый') || name.includes('купец')) return 'Торговое судно';
  return 'Локация';
};

const getSpawnPointIcon = (type) => {
  const t = type.toLowerCase();
  if (t.includes('корабль') || t.includes('судно') || t.includes('carrier') || t.includes('ship')) return <FlightIcon />;
  if (t.includes('станция') || t.includes('station')) return <BusinessIcon />;
  if (t.includes('порт') || t.includes('port')) return <FlightIcon />;
  if (t.includes('военн') || t.includes('military') || t.includes('лагерь') || t.includes('camp')) return <FortIcon />;
  if (t.includes('колония') || t.includes('colony')) return <HomeIcon />;
  return <LocationOnIcon />;
};

// ========================
// КОМПОНЕНТ
// ========================

const CharacterDesigner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const auth = useSelector((state) => state.auth);
  const {
    characters,
    selectedCharacter,
    loading,
    error: errorMessage,
    success: successMessage,
    mode,
  } = useSelector((state) => state.game);

  const systemRole = auth?.access?.system_role || 'user';
  const isAdmin = systemRole === 'admin' || systemRole === 'superadmin';
  const currentKratosId = auth?.access?.kratos_identity_id;

  const [activeTab, setActiveTab] = useState(null);

  // Онтологии
  const [ontologyLoading, setOntologyLoading] = useState(false);
  const [ontologyErrorMessage, setOntologyErrorMessage] = useState('');
  const [races, setRaces] = useState([]);
  const [skills, setSkills] = useState([]);
  const [character_templates, setCharacterTemplates] = useState([]);
  const [traits, setTraits] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [phobias, setPhobias] = useState([]);

  // Чужие персонажи (для admin)
  const [otherCharacters, setOtherCharacters] = useState([]);
  const [loadingOthers, setLoadingOthers] = useState(false);

  // Миры и точки спауна
  const [availableWorlds, setAvailableWorlds] = useState([]);
  const [spawnPoints, setSpawnPoints] = useState([]);
  const [worldSelectionDialogOpen, setWorldSelectionDialogOpen] = useState(false);
  const [selectedWorld, setSelectedWorld] = useState('');
  const [selectedSpawnPoint, setSelectedSpawnPoint] = useState(null);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [placementRetryCount, setPlacementRetryCount] = useState(0);

  // Диалог после создания
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [newCharacter, setNewCharacter] = useState(null);

  const userId = user?.id;
  const MAX_PLACEMENT_RETRIES = 3;

  // ========================
  // ЗАГРУЗКА
  // ========================

  const loadOntologies = useCallback(async () => {
    if (!isAuthenticated) return;
    setOntologyLoading(true);
    setOntologyErrorMessage('');
    try {
      const [
        racesResponse,
        skillsResponse,
        templatesResponse,
        traitsResponse,
        preferencesResponse,
        phobiasResponse,
        worldsResponse,
      ] = await Promise.all([
        Gateway.api.ontology.get('races'),
        Gateway.api.ontology.get('skills'),
        Gateway.api.ontology.get('character_templates'),
        Gateway.api.ontology.get('traits'),
        Gateway.api.ontology.get('preferences'),
        Gateway.api.ontology.get('phobias'),
        Gateway.api.worldManager.getAvailableWorlds(),
      ]);
      setRaces(racesResponse || []);
      setSkills(skillsResponse || []);
      setCharacterTemplates(templatesResponse || []);
      setTraits(traitsResponse || []);
      setPreferences(preferencesResponse || []);
      setPhobias(phobiasResponse || []);
      setAvailableWorlds(Array.isArray(worldsResponse) ? worldsResponse : []);
    } catch (error) {
      console.error('❌ Ошибка загрузки онтологий:', error);
      setOntologyErrorMessage(
        `Не удалось загрузить данные: ${Gateway.utils.getErrorMessage(error)}`
      );
    } finally {
      setOntologyLoading(false);
    }
  }, [isAuthenticated]);

  const loadSpawnPoints = useCallback(
    async (worldId) => {
      if (!worldId || !isAuthenticated) {
        setSpawnPoints([]);
        return;
      }
      try {
        const points = await WorldGateway.getSpawnPoints(worldId);
        setSpawnPoints(points || []);
      } catch (error) {
        console.error('❌ Ошибка загрузки точек спауна:', error);
        setSpawnPoints([]);
        dispatch(
          setError(`Ошибка загрузки точек спауна: ${Gateway.utils.getErrorMessage(error)}`)
        );
      }
    },
    [isAuthenticated, dispatch]
  );

  const loadUserCharacters = useCallback(async () => {
    if (!isAuthenticated || !userId) return;
    try {
      await dispatch(loadCharacters({ userId, mode })).unwrap();
    } catch (error) {
      dispatch(setError(`Ошибка загрузки: ${error.message || error}`));
    }
  }, [isAuthenticated, userId, dispatch, mode]);

  const loadOtherCharacters = useCallback(async () => {
    if (!isAdmin || !isAuthenticated) return;
    setLoadingOthers(true);
    try {
      const users = await Gateway.api.accessAdmin.listUsers();
      const others = [];
      for (const u of users || []) {
        if (!u || u.kratos_identity_id === currentKratosId) continue;
        for (const c of u.characters || []) {
          const charId = c.character_id || c.image_bit_id || c;
          if (!charId) continue;
          others.push({
            character_id: charId,
            image_bit_id: c.image_bit_id || charId,
            name: c.name || null,
            status: c.status || 'unknown',
            source: c.source || 'unknown',
            _ownerEmail: u.email,
            _ownerDisplayName: u.display_name || u.email,
            _ownerKratosId: u.kratos_identity_id,
            _isOther: true,
          });
        }
      }
      setOtherCharacters(others);
    } catch (err) {
      console.error('❌ loadOtherCharacters:', err);
    } finally {
      setLoadingOthers(false);
    }
  }, [isAdmin, isAuthenticated, currentKratosId]);

  const forceRefreshCharacters = async () => {
    if (!userId) return;
    try {
      await dispatch(loadCharacters({ userId, mode, force: true })).unwrap();
    } catch (error) {
      console.error('❌ Ошибка обновления:', error);
    }
  };

  // ========================
  // ЭФФЕКТЫ
  // ========================

  useEffect(() => {
    if (isAuthenticated && Array.isArray(characters) && activeTab === null) {
      const hasValidChars = characters.some((c) => validateId(c));
      setActiveTab(hasValidChars ? TABS.MANAGEMENT : TABS.CREATION);
    }
  }, [isAuthenticated, characters, activeTab]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOntologies();
      loadUserCharacters();
    }
  }, [isAuthenticated, loadOntologies, loadUserCharacters]);

  useEffect(() => {
    if (isAdmin && isAuthenticated) {
      loadOtherCharacters();
    }
  }, [isAdmin, isAuthenticated, loadOtherCharacters]);

  useEffect(() => {
    if (selectedWorld) loadSpawnPoints(selectedWorld);
    else {
      setSpawnPoints([]);
      setSelectedSpawnPoint(null);
    }
  }, [selectedWorld, loadSpawnPoints]);

  // ========================
  // СОЗДАНИЕ ПЕРСОНАЖА
  // ========================

  const handleCharacterCreated = async (createdCharacter) => {
    const characterId = extractId(createdCharacter);
    if (!validateId(characterId)) {
      dispatch(setError('Сервер вернул некорректный ID персонажа'));
      return;
    }

    await forceRefreshCharacters();
    setActiveTab(TABS.MANAGEMENT);

    setNewCharacter({ ...createdCharacter, id: characterId });
    setCreationDialogOpen(true);

    if (isAdmin) {
      loadOtherCharacters();
    }
  };

  const handleActivateNewCharacter = async () => {
    if (!newCharacter) return;
    try {
      const characterId = extractId(newCharacter);
      if (!validateId(characterId)) throw new Error('Неверный ID');
      const hasActive = (characters || []).some((c) => isCharacterActive(c));
      if (hasActive && !window.confirm('У вас уже есть активный персонаж. Активировать нового?')) {
        return;
      }
      await activateCharacterAPI(characterId);
      await forceRefreshCharacters();
      dispatch(setSuccess(`Персонаж "${newCharacter.name}" активирован!`));
      setCreationDialogOpen(false);
      const savedId = characterId;
      setNewCharacter(null);
      setTimeout(() => {
        dispatch(setSelectedCharacter(savedId));
        handleOpenWorldSelection(savedId);
      }, 500);
    } catch (err) {
      console.error('❌ Ошибка активации:', err);
      dispatch(setError(`Ошибка активации: ${err.message || err}`));
      await forceRefreshCharacters();
    }
  };

  const handleStayInDesigner = () => {
    const created = newCharacter;
    setCreationDialogOpen(false);
    setTimeout(() => {
      setActiveTab(TABS.MANAGEMENT);
      const id = extractId(created);
      if (id) setTimeout(() => dispatch(setSelectedCharacter(id)), 100);
    }, 300);
    setNewCharacter(null);
  };

  const handleCloseCreationDialog = () => {
    setCreationDialogOpen(false);
    setNewCharacter(null);
  };

  // ========================
  // РАЗМЕЩЕНИЕ
  // ========================

  const handleOpenWorldSelection = (characterId) => {
    const id = normalizeId(characterId);
    if (!validateId(id)) {
      dispatch(setError('Неверный ID персонажа'));
      return;
    }
    const character = (characters || []).find((c) => extractId(c) === id);
    if (!character) {
      dispatch(setError('Персонаж не найден'));
      return;
    }
    if (!isCharacterActive(character)) {
      dispatch(setError('Персонаж должен быть активирован перед размещением'));
      return;
    }
    dispatch(setSelectedCharacter(id));
    setSelectedSpawnPoint(null);
    setWorldSelectionDialogOpen(true);
    setPlacementRetryCount(0);
    if (availableWorlds.length > 0) setSelectedWorld(availableWorlds[0].id);
    else setSelectedWorld('');
  };

  const handlePlaceCharacterInWorld = async () => {
    if (!selectedSpawnPoint) {
      dispatch(setError('Выберите точку спауна'));
      return;
    }
    const characterId = extractId(selectedCharacter);
    if (!validateId(characterId)) {
      dispatch(setError('Неверный ID персонажа'));
      return;
    }
    const character = (characters || []).find((c) => extractId(c) === characterId);
    if (!character) {
      dispatch(setError('Персонаж не найден'));
      return;
    }
    if (!selectedWorld) {
      dispatch(setError('Выберите мир'));
      return;
    }
    if (!validateStringId(selectedSpawnPoint.id)) {
      dispatch(setError('Некорректный ID точки спауна'));
      return;
    }
    setPlacementLoading(true);
    try {
      const result = await placeCharacterInSpawnPointAPI(characterId, selectedSpawnPoint.id);
      if (result?.status === 'success' || (result && !result.error)) {
        const placementData = result.data || result;
        dispatch(setSuccess(`Персонаж "${character.name}" размещён в ${selectedSpawnPoint.name}!`));
        setWorldSelectionDialogOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 1500));
        await forceRefreshCharacters();
        setTimeout(() => {
          navigate('/game', {
            state: {
              characterId,
              characterName: character.name,
              worldId: placementData.world_id || selectedWorld,
              spawnPointId: placementData.spawn_object_id || selectedSpawnPoint.id,
              position: placementData.position,
              placed: true,
              characterData: {
                ...character,
                spawn_object_id: placementData.spawn_object_id,
                current_world_id: placementData.world_id,
                in_world: true,
              },
            },
          });
        }, 1000);
      } else {
        throw new Error(result?.message || result?.data?.message || 'Неизвестная ошибка размещения');
      }
    } catch (error) {
      const errorResponse = error.response?.data;
      const errorMessage =
        errorResponse?.message || error.message || Gateway.utils.getErrorMessage(error);
      let displayMessage = errorMessage;
      let shouldRetry = false;

      if (errorMessage.includes('not active in simulation')) {
        displayMessage = 'Персонаж не активен в симуляции';
      } else if (errorMessage.includes('not a valid spawn point')) {
        displayMessage = 'Выбранный объект не является точкой спауна';
      } else if (errorMessage.includes('does not belong to you')) {
        displayMessage = 'Персонаж не принадлежит вам';
      } else if (errorMessage.includes('world is not available')) {
        displayMessage = 'Игровой мир недоступен';
        shouldRetry = true;
      } else if (errorMessage.includes('Failed to update')) {
        displayMessage = 'Ошибка сохранения данных';
        shouldRetry = true;
      } else if (errorMessage.includes('timeout') || errorMessage.includes('network')) {
        displayMessage = 'Ошибка сети';
        shouldRetry = true;
      }

      if (shouldRetry && placementRetryCount < MAX_PLACEMENT_RETRIES) {
        const newRetryCount = placementRetryCount + 1;
        setPlacementRetryCount(newRetryCount);
        dispatch(setError(`Попытка ${newRetryCount}/${MAX_PLACEMENT_RETRIES}: ${displayMessage}`));
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, newRetryCount))
        );
        setPlacementLoading(false);
        await handlePlaceCharacterInWorld();
        return;
      }
      dispatch(setError(`Ошибка размещения: ${displayMessage}`));
    } finally {
      setPlacementLoading(false);
    }
  };

  // ========================
  // РЕДАКТОР / УПРАВЛЕНИЕ
  // ========================

  const handleEditCharacter = (characterId) => {
    const id = normalizeId(characterId);
    if (validateId(id)) {
      dispatch(setSelectedCharacter(id));
      setActiveTab(TABS.EDITOR);
    } else {
      dispatch(setError('Не удалось выбрать персонажа'));
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    if (!window.confirm('Удалить персонажа?')) return;
    try {
      const id = normalizeId(characterId);
      if (!validateId(id)) throw new Error(`Неверный ID: ${characterId}`);
      await dispatch(deleteCharacter(id)).unwrap();
      dispatch(setSuccess('Персонаж удален'));
    } catch (error) {
      dispatch(setError(`Ошибка удаления: ${error.message || error}`));
    }
  };

  const handleEditorSaved = async () => {
    await forceRefreshCharacters();
    dispatch(setSuccess('Изменения сохранены'));
    setActiveTab(TABS.MANAGEMENT);
  };

  const handleActivateCharacter = async (characterId) => {
    try {
      const id = normalizeId(characterId);
      if (!validateId(id)) throw new Error(`Неверный ID: ${characterId}`);
      await activateCharacterAPI(id);
      dispatch(setSuccess('Активирован'));
      await forceRefreshCharacters();
    } catch (error) {
      dispatch(setError(`Ошибка активации: ${error.message || error}`));
      await forceRefreshCharacters();
    }
  };

  const handleDeactivateCharacter = async (characterId) => {
    try {
      const id = normalizeId(characterId);
      if (!validateId(id)) throw new Error(`Неверный ID: ${characterId}`);
      await deactivateCharacterAPI(id);
      dispatch(setSuccess('Деактивирован'));
      await forceRefreshCharacters();
    } catch (error) {
      dispatch(setError(`Ошибка деактивации: ${error.message || error}`));
      await forceRefreshCharacters();
    }
  };

  const handleRefresh = async () => {
    await Promise.all([
      loadUserCharacters(),
      loadOntologies(),
      isAdmin ? loadOtherCharacters() : Promise.resolve(),
    ]);
  };

  const handleAfterAdminChange = async () => {
    await forceRefreshCharacters();
    if (isAdmin) {
      await loadOtherCharacters();
    }
  };

  const validCharacters = (characters || []).filter((char) => validateId(char));
  const selectedCharacterId = normalizeId(selectedCharacter);
  const currentSelectedCharacter = (characters || []).find(
    (c) => extractId(c) === selectedCharacterId
  );

  // ========================
  // РЕНДЕР
  // ========================

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Требуется авторизация</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Диалог после создания */}
      <Dialog open={creationDialogOpen} onClose={handleCloseCreationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          🎉 Персонаж создан!
          <IconButton onClick={handleCloseCreationDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {newCharacter && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                {newCharacter.name || 'Без имени'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, color: 'primary.contrastText' }}>
                {newCharacter.race || 'Раса не указана'} • Уровень {newCharacter.level || 1}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                ID: {extractId(newCharacter)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<GamepadIcon />}
              onClick={handleActivateNewCharacter}
              fullWidth
              disabled={!newCharacter || !validateId(newCharacter)}
            >
              🚀 Активировать и разместить в мире
            </Button>
            <Divider sx={{ my: 1 }}>или</Divider>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleStayInDesigner} fullWidth>
              📝 Остаться в конструкторе
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreationDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог размещения */}
      <Dialog
        open={worldSelectionDialogOpen}
        onClose={() => setWorldSelectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketLaunchIcon color="primary" />
            🌍 Размещение персонажа в мире
          </Box>
          <IconButton
            onClick={() => setWorldSelectionDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentSelectedCharacter && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {currentSelectedCharacter.name}
              </Typography>
              <Typography variant="body2">
                {currentSelectedCharacter.race} • Уровень {currentSelectedCharacter.level}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={isCharacterActive(currentSelectedCharacter) ? 'Активен' : 'Не активен'}
                  size="small"
                  color={isCharacterActive(currentSelectedCharacter) ? 'success' : 'warning'}
                  variant="outlined"
                  icon={isCharacterActive(currentSelectedCharacter) ? <CheckCircleIcon /> : <WarningIcon />}
                />
                {isCharacterPlacedInWorld(currentSelectedCharacter) && (
                  <Chip label="Уже размещён" size="small" color="success" variant="filled" icon={<CheckCircleIcon />} />
                )}
              </Box>
            </Box>
          )}

          {placementRetryCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Попытка размещения {placementRetryCount}/{MAX_PLACEMENT_RETRIES}
              <LinearProgress
                variant="determinate"
                value={(placementRetryCount / MAX_PLACEMENT_RETRIES) * 100}
                sx={{ mt: 1 }}
              />
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Выберите мир</InputLabel>
                <Select
                  value={selectedWorld}
                  onChange={(e) => setSelectedWorld(e.target.value)}
                  label="Выберите мир"
                  disabled={placementLoading}
                >
                  <MenuItem value=""><em>Не выбран</em></MenuItem>
                  {availableWorlds.map((world) => (
                    <MenuItem key={world.id} value={world.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {world.is_public ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                        <span>{world.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {availableWorlds.length === 0 && (
                <Alert severity="info">Нет доступных миров.</Alert>
              )}
              {selectedWorld && spawnPoints.length === 0 && (
                <Alert severity="warning">В этом мире нет точек спауна.</Alert>
              )}
            </Grid>
          </Grid>

          {selectedWorld && spawnPoints.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ExploreIcon /> Доступные точки спауна ({spawnPoints.length})
              </Typography>
              <RadioGroup
                value={selectedSpawnPoint?.id || ''}
                onChange={(e) => {
                  const point = spawnPoints.find((p) => normalizeId(p.id) === e.target.value);
                  setSelectedSpawnPoint(point || null);
                }}
              >
                <List dense>
                  {spawnPoints.map((point) => {
                    const displayType = getSpawnPointType(point);
                    const TypeIcon = getSpawnPointIcon(displayType);
                    const pointId = normalizeId(point.id);
                    return (
                      <ListItem
                        key={pointId}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: selectedSpawnPoint?.id === pointId ? 'action.selected' : 'background.paper',
                          border: '1px solid',
                          borderColor: selectedSpawnPoint?.id === pointId ? 'primary.main' : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{TypeIcon}</ListItemIcon>
                        <FormControlLabel
                          value={pointId}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {point.name}
                                </Typography>
                                <Chip label={displayType} size="small" color="info" variant="outlined" />
                                {point.is_safe && (
                                  <Chip label="Безопасная" size="small" color="success" variant="filled" />
                                )}
                              </Box>
                              {point.description && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  {point.description}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </RadioGroup>
            </Box>
          )}

          {selectedSpawnPoint && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.dark' }}>
                <CheckCircleIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Выбрана точка: <strong>{selectedSpawnPoint.name}</strong> ({getSpawnPointType(selectedSpawnPoint)})
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorldSelectionDialogOpen(false)} disabled={placementLoading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handlePlaceCharacterInWorld}
            disabled={!selectedSpawnPoint || placementLoading}
            startIcon={placementLoading ? <CircularProgress size={20} /> : <RocketLaunchIcon />}
          >
            {placementLoading
              ? `Размещение... (${placementRetryCount}/${MAX_PLACEMENT_RETRIES})`
              : 'Разместить персонажа'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearSuccess())}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {typeof errorMessage === 'string' ? errorMessage : 'Произошла ошибка'}
        </Alert>
      )}

      {/* Панель */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon /> Конструктор персонажей
          </Typography>
          <Box>
            <Tooltip title="Обновить данные">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading || ontologyLoading || loadingOthers}
                sx={{ mr: 1 }}
              >
                Обновить
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setActiveTab(TABS.CREATION)}
              disabled={ontologyLoading}
            >
              Создать
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Вкладки */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab === null ? 0 : activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<AddIcon />} label="Создание" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="Управление" iconPosition="start" />
          <Tab
            icon={<EditIcon />}
            label="Редактор"
            iconPosition="start"
            disabled={!selectedCharacter}
          />
        </Tabs>
      </Paper>

      {/* Содержимое */}
      {(loading || ontologyLoading) && activeTab !== TABS.CREATION && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>Загрузка...</Typography>
        </Box>
      )}

      {activeTab === TABS.CREATION && (
        <CharacterCreation onCharacterCreated={handleCharacterCreated} />
      )}

      {activeTab === TABS.MANAGEMENT && !loading && (
        <Box>
          {validCharacters.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {characters.length > 0 ? 'Нет валидных персонажей' : 'Нет персонажей'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setActiveTab(TABS.CREATION)}
                sx={{ mt: 2 }}
              >
                Создать персонажа
              </Button>
            </Paper>
          ) : (
            <>
              <Grid container spacing={2}>
                {validCharacters.map((char, idx) => {
                  const id = extractId(char);
                  return (
                    <Grid item xs={12} md={6} lg={4} key={id || idx}>
                      <CharacterCard
                        character={char}
                        isSelected={selectedCharacterId === id}
                        onSelect={() => dispatch(setSelectedCharacter(id))}
                        onEdit={() => handleEditCharacter(id)}
                        onDelete={() => handleDeleteCharacter(id)}
                        onActivate={() => handleActivateCharacter(id)}
                        onDeactivate={() => handleDeactivateCharacter(id)}
                        showActivation
                        races={races}
                        index={idx}
                      />
                      {isAdmin && (
                        <CharacterAdminActions
                          character={{ ...char, character_id: id }}
                          isOwn={true}
                          onAfterChange={handleAfterAdminChange}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>

              {currentSelectedCharacter && (
                <Paper
                  sx={{
                    mt: 3,
                    p: 3,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Управление: {currentSelectedCharacter.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color={isCharacterActive(currentSelectedCharacter) ? 'secondary' : 'success'}
                      startIcon={isCharacterActive(currentSelectedCharacter) ? <PauseIcon /> : <PlayArrowIcon />}
                      onClick={() =>
                        isCharacterActive(currentSelectedCharacter)
                          ? handleDeactivateCharacter(currentSelectedCharacter.id)
                          : handleActivateCharacter(currentSelectedCharacter.id)
                      }
                      disabled={placementLoading}
                    >
                      {isCharacterActive(currentSelectedCharacter) ? 'Деактивировать' : 'Активировать'}
                    </Button>
                    <Tooltip
                      title={
                        !isCharacterActive(currentSelectedCharacter)
                          ? 'Сначала активируйте персонажа'
                          : 'Разместить в мире'
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<LocationOnIcon />}
                          onClick={() => handleOpenWorldSelection(currentSelectedCharacter.id)}
                          disabled={!isCharacterActive(currentSelectedCharacter) || placementLoading}
                        >
                          Разместить в мире
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditCharacter(currentSelectedCharacter.id)}
                      disabled={placementLoading}
                    >
                      Редактировать
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: isCharacterPlacedInWorld(currentSelectedCharacter)
                        ? 'success.light'
                        : 'warning.light',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isCharacterPlacedInWorld(currentSelectedCharacter) ? (
                        <>
                          <CheckCircleIcon color="success" />
                          <Typography variant="body2" color="success.dark">
                            <strong>Размещён в мире</strong> • ID: {currentSelectedCharacter.spawn_object_id}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <WarningIcon color="warning" />
                          <Typography variant="body2" color="warning.dark">
                            <strong>Не размещён</strong> • Активируйте и выберите точку спауна
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </>
          )}

          {isAdmin && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon /> Персонажи других игроков ({otherCharacters.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Только для просмотра. Можно освободить или передать владельцу.
              </Typography>

              {loadingOthers ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : otherCharacters.length === 0 ? (
                <Alert severity="info">Нет чужих персонажей.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {otherCharacters.map((char, idx) => (
                    <Grid item xs={12} md={6} lg={4} key={char.character_id || idx}>
                      <Paper
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'warning.light',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} noWrap>
                              {char.name || char.character_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              ID: {char.character_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Владелец: {char._ownerDisplayName || char._ownerEmail || '?'}
                            </Typography>
                          </Box>
                          <Chip
                            label={char.status || 'unknown'}
                            size="small"
                            color={STATUS_COLORS[char.status] || 'default'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <Chip label="Чужой" size="small" color="warning" icon={<LockIcon />} />
                        </Box>
                        <CharacterAdminActions
                          character={char}
                          isOwn={false}
                          onAfterChange={handleAfterAdminChange}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      )}

      {activeTab === TABS.EDITOR && selectedCharacterId && (
        <CharacterEditor
          character={currentSelectedCharacter || null}
          onSave={handleEditorSaved}
          onCancel={() => setActiveTab(TABS.MANAGEMENT)}
          onBack={() => setActiveTab(TABS.MANAGEMENT)}
          races={races}
          skills={skills}
          templates={character_templates}
          traits={traits}
          preferences={preferences}
          phobias={phobias}
        />
      )}

      {ontologyErrorMessage && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {ontologyErrorMessage}
          <Button size="small" onClick={loadOntologies} sx={{ mt: 1 }}>
            Повторить
          </Button>
        </Alert>
      )}
    </Container>
  );
};

export {
  activateCharacterAPI,
  deactivateCharacterAPI,
  extractId,
  validateId,
  normalizeId,
  validateStringId,
  isCharacterActive,
  isCharacterPlacedInWorld,
  placeCharacterInSpawnPointAPI,
};

export default CharacterDesigner;
